import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Form, Input, InputNumber, Select } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { withRoleGuard } from 'shared/hocs/with-role-guard';
import { useRoleGuard } from 'shared/hooks/use-role-guard';
import { ClientType } from 'types/client.ts';
import { TransportType } from 'types/transport.ts';
import { UserRole } from 'types/user';
import { ClientFormSelect } from 'components/common/client-form-select';
import { TransportSubTypeInput } from 'components/transport/transport-form/components/transport-subtype-input';
import type { ITransportFormProps } from 'components/transport/transport-form/types';
import { validationRule } from 'components/transport/transport-form/utils';

export const TransportFormComponent: React.FC<ITransportFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, isEditMode } = props;
  const { t } = useTranslation();
  const isMainInputAllowed = useRoleGuard([UserRole.admin]);

  const handleExclusiveCheckboxChange = (uncheckedFieldName: string) => (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      form.setFieldsValue({ [uncheckedFieldName]: false });
    }
  };
  const transportTypeSelected = Form.useWatch('type', form);
  const needServiceChecked = Form.useWatch('needService', form);
  const inServiceChecked = Form.useWatch('inService', form);
  const showServiceComment = needServiceChecked || inServiceChecked;

  const handleTypeChange = () => {
    form.setFieldsValue({ subType: undefined });
  };

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      {isMainInputAllowed && (
        <>
          <Form.Item name='plateNumber' label={t('transport:plate_number')} rules={validationRule.plateNumber}>
            <Input placeholder={t('transport:plate_number')} />
          </Form.Item>

          <ClientFormSelect
            name='subcontractor'
            label={t('transport:subcontractor')}
            placeholder={t('transport:subcontractor')}
            rules={validationRule.subcontractor}
            currentClientId={initialValues?.subcontractor?.value}
            clientTypes={[ClientType.carrier]}
            allowClear
          />

          <Form.Item name='type' label={t('common:type')} rules={validationRule.type}>
            <Select placeholder={t('common:type')} onChange={handleTypeChange}>
              <Select.Option value={TransportType.truck}>{TransportType.truck}</Select.Option>
              <Select.Option value={TransportType.trailer}>{TransportType.trailer}</Select.Option>
            </Select>
          </Form.Item>
          <TransportSubTypeInput type={transportTypeSelected} validationRule={validationRule.subType} />
          <Form.Item
            name='defaultCostPerKm'
            label={t('transport:default_cost_per_km')}
            rules={[{ required: transportTypeSelected === TransportType.truck, type: 'number' }]}>
            <InputNumber
              min={0}
              placeholder={t('transport:default_cost_per_km')}
              decimalSeparator={','}
              controls={false}
            />
          </Form.Item>
          <Form.Item name='maxLDM' label={t('transport:max_ldm')} rules={validationRule.maxLDM}>
            <InputNumber placeholder={t('transport:max_ldm')} decimalSeparator={','} controls={false} />
          </Form.Item>
          <Form.Item name='maxWeight' label={t('transport:max_weight')} rules={validationRule.maxWeight}>
            <InputNumber placeholder={t('transport:max_weight')} controls={false} />
          </Form.Item>
          <Form.Item name='maxVolume' label={t('transport:max_volume')} rules={validationRule.maxVolume}>
            <InputNumber placeholder={t('transport:max_volume')} decimalSeparator={','} controls={false} />
          </Form.Item>
        </>
      )}
      {isEditMode && (
        <>
          <Form.Item name='needService' valuePropName='checked' rules={validationRule.needService}>
            <Checkbox onChange={handleExclusiveCheckboxChange('inService')}>{t('transport:needs_service')}</Checkbox>
          </Form.Item>
          <Form.Item name='inService' valuePropName='checked' rules={validationRule.inService}>
            <Checkbox onChange={handleExclusiveCheckboxChange('needService')}>{t('transport:in_service')}</Checkbox>
          </Form.Item>
          {showServiceComment && (
            <Form.Item name='serviceComment' label={t('transport:service_comment')}>
              <Input.TextArea placeholder={t('transport:service_comment')} rows={3} />
            </Form.Item>
          )}
          {isMainInputAllowed && (
            <Form.Item name='notUsed' valuePropName='checked' rules={validationRule.notUsed}>
              <Checkbox>{t('transport:not_used')}</Checkbox>
            </Form.Item>
          )}
        </>
      )}
    </Form>
  );
};

export const TransportForm = withRoleGuard<ITransportFormProps>(TransportFormComponent, [
  UserRole.admin,
  UserRole.mechanic,
]);
